import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { Campanha } from '@eleg360/types';
import { campanhasApi } from '@/lib/api/campanhas';
import { contatosApi } from '@/lib/api/contatos';
import { adminApi } from '@/lib/api/admin';
import { useAuthStore } from '@/lib/stores/authStore';
import { useBairros } from '@/lib/hooks/useBairros';
import { toast } from '@/lib/stores/toastStore';
import { BASE_LEGAL_ENVIO, podeEnviarWhatsApp } from '@/lib/lgpd';
import {
  ESCALAS,
  TAXA_RESPOSTA_REFERENCIA,
  cpLeitura,
  motivoPublicoIncoerente,
  simularResultados,
  sugerirPerguntas,
  taxaRespostaHistorica,
  type ModoPublico,
  type Pergunta,
  type PublicoDef,
  type ResultadoCampanha,
  type TemaPerguntas,
  type TipoEscala,
} from './dados';
import { PreviaWhatsApp } from './PreviaWhatsApp';
import { ChatCampanha } from './ChatCampanha';
import { Info } from '@/design-system/Info';

const TAGS = ['Saúde', 'Iluminação', 'Creche', 'Liderança', 'Comércio', 'Juventude', 'Educação'];

const OBJETIVOS_SUGERIDOS = [
  'Segurança em Messejana: forças externas, facções como terroristas, policiamento x social',
  'Saúde: fila da UBS, abrir aos sábados, telemedicina',
  'Creche em tempo integral em Jangurussu',
  'Maior necessidade do bairro e presença da vereadora',
];

const ROTULOS_ESCALA: Record<TipoEscala, string> = {
  likert: 'Concordância (5 níveis)',
  sn: 'Sim / Não / Não sei',
  prio: 'Prioridade (escolha 1)',
  nota: 'Nota 1–5',
  aberta: 'Resposta aberta',
};

const MODOS_PUBLICO: { modo: ModoPublico; rotulo: string }[] = [
  { modo: 'bairro', rotulo: 'Só um bairro' },
  { modo: 'mun', rotulo: 'Município inteiro' },
  { modo: 'uf', rotulo: 'Estado' },
  { modo: 'todos', rotulo: 'Todos os meus contatos' },
  { modo: 'amostra', rotulo: 'Amostra aleatória' },
];

/** Limite de contatos buscados para compor a distribuição por bairro da simulação. */
const LIMITE_BUSCA_PUBLICO = 1000;

interface WizardCampanhaProps {
  aoConcluir: (c: Campanha) => void;
}

/**
 * Wizard de 4 etapas de "Campanhas estratégicas" (proto:1619-1636, `cpRender`).
 *
 * Etapas 0-2 produzem uma campanha real (`campanhasApi.create` com
 * `tipo: 'estrategica'`), sobre público real (contatos com opt-in). A etapa 3
 * (resultados) usa `simularResultados`, que é uma demonstração — o próprio
 * protótipo avisa que "resultados [são] simulados para o protótipo" e que a
 * leitura real chegaria pelo webhook de respostas (proto:1628). Por isso o
 * rótulo de simulação aparece também aqui, em vez de apresentar os números
 * como se fossem respostas reais já coletadas.
 */
export function WizardCampanha({ aoConcluir }: WizardCampanhaProps) {
  const token = useAuthStore((s) => s.token) || '';

  const { bairros } = useBairros();
  const { data: mandato } = useQuery({
    queryKey: ['mandato'],
    queryFn: () => adminApi.getMandato(token),
    enabled: !!token,
  });
  const limiar = mandato?.configuracoes?.limiar_campanha ?? 100;

  const [etapa, setEtapa] = useState(0);
  const [localidade, setLocalidade] = useState('');
  const [objetivo, setObjetivo] = useState('');
  const [tema, setTema] = useState<TemaPerguntas>('geral');
  const [perguntas, setPerguntas] = useState<Pergunta[]>([]);
  const [publico, setPublico] = useState<PublicoDef>({ modo: 'bairro', amostra: 200 });
  const [resultado, setResultado] = useState<ResultadoCampanha | null>(null);
  const [historicoId, setHistoricoId] = useState(1);

  const nomeBairro = (id: string) => bairros.find((b) => b.id === id)?.n || id;

  const perguntasAtivas = perguntas.filter((p) => p.on);

  // A regra de base legal é uma só, importada de `lib/lgpd` — a mesma que o
  // servidor aplica em `calcularPublicoAlvo` e que o worker reconfere no envio.
  const filtrosBusca: Record<string, any> = {
    base_legal: BASE_LEGAL_ENVIO,
    limit: LIMITE_BUSCA_PUBLICO,
  };
  if (publico.tag) filtrosBusca.tag = publico.tag;
  if (publico.modo === 'bairro') filtrosBusca.bairro = publico.bairro || localidade;
  if (publico.modo === 'mun') filtrosBusca.municipio = publico.municipio;
  if (publico.modo === 'uf') filtrosBusca.uf = publico.uf;

  const { data: previaPublico } = useQuery({
    queryKey: ['contatos', 'campanha-estrategica', filtrosBusca],
    queryFn: () => contatosApi.list(filtrosBusca, token),
    enabled: !!token && etapa === 2,
  });

  // Reconferência local pela MESMA função que decide o envio de WhatsApp: se o
  // servidor um dia devolver alguém sem opt-in, essa pessoa não entra na conta
  // nem na distribuição exibida aqui.
  const itens = (previaPublico?.items || []).filter(podeEnviarWhatsApp);
  const totalReal = previaPublico?.total ?? 0;

  // Taxa de resposta: dado REAL das campanhas do mandato (respostas/enviadas).
  // `null` quando nenhuma campanha registrou envio — aí a tela declara a
  // lacuna em vez de apresentar a constante do protótipo como "histórico".
  const { data: campanhasDoMandato = [] } = useQuery({
    queryKey: ['campanhas'],
    queryFn: () => campanhasApi.list(token),
    enabled: !!token,
  });
  const historico = taxaRespostaHistorica(campanhasDoMandato);
  const taxaUsada = historico
    ? { valor: historico.taxa, origem: 'historico' as const }
    : { valor: TAXA_RESPOSTA_REFERENCIA, origem: 'referencia_nao_calculada' as const };
  const amostraSorteada =
    publico.modo === 'amostra'
      ? [...itens].sort(() => Math.random() - 0.5).slice(0, publico.amostra)
      : itens;
  const totalPublico = publico.modo === 'amostra' ? Math.min(publico.amostra, totalReal) : totalReal;
  const bairrosCobertos = [...new Set(amostraSorteada.map((c) => c.bairro).filter(Boolean))] as string[];

  const criar = useMutation({
    mutationFn: (dados: Parameters<typeof campanhasApi.create>[0]) => campanhasApi.create(dados, token),
  });

  const pedirPerguntas = () => {
    const objetivoFinal = objetivo.trim() || 'Maior necessidade do bairro';
    setObjetivo(objetivoFinal);
    const loc = localidade || bairros[0]?.id || '';
    setLocalidade(loc);
    setPublico((p) => ({ ...p, bairro: p.bairro || loc }));
    const { tema: temaDetectado, perguntas: sugeridas } = sugerirPerguntas(objetivoFinal);
    setTema(temaDetectado);
    setPerguntas(sugeridas);
    setEtapa(1);
  };

  const definirPublico = () => {
    if (!perguntasAtivas.length) {
      toast('Ative ao menos uma pergunta antes de definir o público.');
      return;
    }
    setEtapa(2);
  };

  const aprovarEEnviar = async () => {
    if (!perguntasAtivas.length) {
      toast('Ative ao menos uma pergunta antes de enviar.');
      return;
    }
    // Recorte incoerente é recusado, nunca ampliado: modo sem os campos que o
    // definem (ou amostra <= 0) não vira "nenhuma cláusula" — que seria o
    // mandato inteiro. O servidor recusa de qualquer forma; aqui a pessoa
    // descobre qual campo falta antes de disparar.
    const incoerente = motivoPublicoIncoerente(publico);
    if (incoerente) {
      toast(incoerente);
      return;
    }
    if (totalPublico === 0) {
      toast('Nenhum contato com opt-in no público escolhido.');
      return;
    }
    const publicoDef: Record<string, any> = { modo: publico.modo, tag: publico.tag || undefined };
    if (publico.modo === 'bairro') publicoDef.bairro = publico.bairro || localidade;
    if (publico.modo === 'mun') {
      publicoDef.municipio = publico.municipio;
      publicoDef.uf = publico.uf;
    }
    if (publico.modo === 'uf') publicoDef.uf = publico.uf;
    if (publico.modo === 'amostra') publicoDef.amostra = publico.amostra;

    try {
      const campanha = await criar.mutateAsync({
        nome: `Campanha estratégica: ${objetivo.slice(0, 60)}`,
        tipo: 'estrategica',
        publico_def: publicoDef,
        mensagem_texto: `Questionário de campanha estratégica (${perguntasAtivas.length} perguntas): ${objetivo}`,
      });
      const id = `CE-${historicoId}`;
      setHistoricoId((n) => n + 1);
      const r = simularResultados(
        amostraSorteada.length ? amostraSorteada : itens,
        perguntasAtivas,
        { id, data: new Date().toLocaleDateString('pt-BR') },
        11,
        taxaUsada,
      );
      // O total de convidados vem do servidor (honesto mesmo quando a busca
      // de distribuição por bairro foi limitada a LIMITE_BUSCA_PUBLICO).
      setResultado({
        ...r,
        convidados: totalPublico,
        respostas: Math.round(totalPublico * taxaUsada.valor),
      });
      setEtapa(3);
      toast('Campanha enviada · respostas chegam pelo webhook · resultados simulados para demonstração.');
      aoConcluir(campanha);
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      toast(Array.isArray(msg) ? msg.join(' · ') : msg || 'Erro ao criar a campanha estratégica.');
    }
  };

  const novaCampanha = () => {
    setEtapa(0);
    setResultado(null);
    setObjetivo('');
    setPerguntas([]);
  };

  return (
    <div>
      <div className="steps">
        <span className={etapa > 0 ? 'done' : 'cur'}>
          <b>{etapa > 0 ? '✓' : '1'}</b>Objetivo
        </span>
        <em />
        <span className={etapa > 1 ? 'done' : etapa === 1 ? 'cur' : ''}>
          <b>{etapa > 1 ? '✓' : '2'}</b>Perguntas (IA)
        </span>
        <em />
        <span className={etapa > 2 ? 'done' : etapa === 2 ? 'cur' : ''}>
          <b>{etapa > 2 ? '✓' : '3'}</b>Público e envio
        </span>
        <em />
        <span className={etapa === 3 ? 'cur' : ''}>
          <b>4</b>Resultados e leitura
        </span>
      </div>
      <p style={{ fontSize: 12, color: 'var(--ink3)', marginTop: 6 }}>Etapa {etapa + 1} de 4</p>

      {etapa === 0 && (
        <div className="grid g21" style={{ marginTop: 12 }}>
          <div className="card">
            <h2>O que você quer descobrir?</h2>
            <p style={{ fontSize: 13, color: 'var(--ink2)' }}>
              Descreva o objetivo em linguagem natural. O OTO monta um questionário com{' '}
              <b>escalas</b> (concordo totalmente → discordo totalmente, prioridades, notas,
              aberta), porque &quot;sim ou não&quot; não captura quem concorda com o fim mas não
              com o meio.
            </p>
            <div className="field" style={{ marginBottom: 8 }}>
              <label>Localidade que o agente vai analisar para montar as perguntas</label>
              <select className="in" value={localidade} onChange={(e) => setLocalidade(e.target.value)}>
                <option value="">Selecione…</option>
                {bairros.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.n}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Tema / objetivo</label>
            </div>
            <textarea
              className="in"
              style={{ minHeight: 90 }}
              placeholder="Ex.: Entender o que Messejana pensa sobre segurança: presença de forças externas, tratamento das facções, policiamento x projetos sociais."
              value={objetivo}
              onChange={(e) => setObjetivo(e.target.value)}
            />
            <div className="filters" style={{ marginTop: 8 }}>
              {OBJETIVOS_SUGERIDOS.map((q) => (
                <span
                  key={q}
                  className="chip neutral chipbtn"
                  role="button"
                  tabIndex={0}
                  onClick={() => setObjetivo(q)}
                >
                  {q.slice(0, 38)}…
                </span>
              ))}
            </div>
            <button type="button" className="btn pri" style={{ marginTop: 12 }} onClick={pedirPerguntas}>
              Pedir perguntas ao OTO
            </button>
          </div>
        </div>
      )}

      {etapa === 1 && (
        <div className="grid g21" style={{ marginTop: 12 }}>
          <div className="card">
            <h2>
              Perguntas sugeridas pelo OTO{' '}
              <Info titulo="Como a IA lê as respostas">
                Cada resposta é um nível, não um voto. A IA cruza as afirmações (por exemplo,
                concorda que facções são terroristas, mas discorda de forças externas no bairro) e
                descreve por bairro os grupos que aparecem.
              </Info>
              <span className="r" style={{ color: 'var(--ink3)' }}>
                {perguntasAtivas.length} ativas
              </span>
            </h2>
            <p style={{ fontSize: 12, color: 'var(--ink3)' }}>
              Objetivo: <i>{objetivo}</i>. Edite o texto, troque a escala, desligue ou adicione.
            </p>

            {perguntas.map((p, i) => (
              <div
                key={p.id}
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                  padding: '10px 0',
                  borderBottom: '1px solid var(--line)',
                  opacity: p.on ? 1 : 0.45,
                }}
              >
                <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input
                    type="checkbox"
                    checked={p.on}
                    aria-label={`Ativar pergunta: ${p.q}`}
                    onChange={() =>
                      setPerguntas((ps) => ps.map((x, j) => (j === i ? { ...x, on: !x.on } : x)))
                    }
                  />
                </label>
                <div style={{ flex: 1 }}>
                  <input
                    className="in"
                    style={{ padding: '6px 9px' }}
                    value={p.q}
                    aria-label={`Texto da pergunta ${i + 1}`}
                    onChange={(e) =>
                      setPerguntas((ps) => ps.map((x, j) => (j === i ? { ...x, q: e.target.value } : x)))
                    }
                  />
                  <div className="filters" style={{ marginTop: 6 }}>
                    <select
                      className="sel"
                      aria-label={`Escala da pergunta ${i + 1}`}
                      value={p.e}
                      onChange={(e) =>
                        setPerguntas((ps) =>
                          ps.map((x, j) =>
                            j === i ? { ...x, e: e.target.value as TipoEscala, opts: undefined } : x,
                          ),
                        )
                      }
                    >
                      {(Object.keys(ESCALAS) as TipoEscala[]).map((k) => (
                        <option key={k} value={k}>
                          {ROTULOS_ESCALA[k]}
                        </option>
                      ))}
                    </select>
                    <span style={{ fontSize: 11, color: 'var(--ink3)' }}>
                      {(p.opts || ESCALAS[p.e]).join(' · ')}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn sm"
                  aria-label={`Remover pergunta ${i + 1}`}
                  onClick={() => setPerguntas((ps) => ps.filter((_, j) => j !== i))}
                >
                  ✕
                </button>
              </div>
            ))}

            <div className="filters" style={{ marginTop: 10 }}>
              <button
                type="button"
                className="btn sm"
                onClick={() =>
                  setPerguntas((ps) => [
                    ...ps,
                    { id: `P${ps.length + 1}`, q: 'Nova pergunta', e: 'likert', on: true },
                  ])
                }
              >
                + Adicionar pergunta
              </button>
              <button
                type="button"
                className="btn sm"
                onClick={() => {
                  const { tema: t, perguntas: novas } = sugerirPerguntas(`${objetivo} geral`);
                  setTema(t);
                  setPerguntas(novas);
                }}
              >
                Sugerir de novo
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PreviaWhatsApp
              nomeTitular={mandato?.nome || ''}
              cargoTitular={mandato?.cargo || ''}
              nomeLocalidade={nomeBairro(localidade)}
              objetivo={objetivo}
              perguntasAtivas={perguntasAtivas}
            />
            {/*
              Sem pergunta ativa não há questionário: avançar daqui levaria o
              usuário a uma etapa de público que só descobriria o problema no
              clique de "Aprovar e enviar".
            */}
            <button type="button" className="btn pri" onClick={definirPublico}>
              Definir público →
            </button>
            {!perguntasAtivas.length && (
              <p style={{ fontSize: 12, color: 'var(--crit)', margin: 0 }}>
                Nenhuma pergunta ativa. Ative ao menos uma para definir o público.
              </p>
            )}
          </div>
        </div>
      )}

      {etapa === 2 && (
        <div className="grid g21" style={{ marginTop: 12 }}>
          <div className="card">
            <h2>Para quem enviar</h2>
            <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
              <legend style={{ fontSize: 12, color: 'var(--ink3)' }}>Modo de público</legend>
              <div className="filters" style={{ marginBottom: 8 }}>
                {MODOS_PUBLICO.map(({ modo, rotulo }) => (
                  <label key={modo} className={`chip neutral chipbtn ${publico.modo === modo ? 'on' : ''}`}>
                    <input
                      type="radio"
                      name="modoPublico"
                      value={modo}
                      checked={publico.modo === modo}
                      aria-label={`Modo de público: ${rotulo}`}
                      onChange={() => setPublico((p) => ({ ...p, modo }))}
                      style={{ marginRight: 4 }}
                    />
                    {rotulo}
                  </label>
                ))}
              </div>
            </fieldset>

            <div className="filters">
              {publico.modo === 'bairro' && (
                <select
                  className="sel"
                  value={publico.bairro || localidade}
                  onChange={(e) => setPublico((p) => ({ ...p, bairro: e.target.value }))}
                >
                  {bairros.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.n}
                    </option>
                  ))}
                </select>
              )}
              {publico.modo === 'mun' && (
                <>
                  <input
                    className="in"
                    style={{ width: 160 }}
                    placeholder="Município"
                    value={publico.municipio || ''}
                    onChange={(e) => setPublico((p) => ({ ...p, municipio: e.target.value }))}
                  />
                  <input
                    className="in"
                    style={{ width: 60 }}
                    placeholder="UF"
                    maxLength={2}
                    value={publico.uf || ''}
                    onChange={(e) => setPublico((p) => ({ ...p, uf: e.target.value.toUpperCase() }))}
                  />
                </>
              )}
              {publico.modo === 'uf' && (
                <input
                  className="in"
                  style={{ width: 60 }}
                  placeholder="UF"
                  maxLength={2}
                  value={publico.uf || ''}
                  onChange={(e) => setPublico((p) => ({ ...p, uf: e.target.value.toUpperCase() }))}
                />
              )}
              {publico.modo === 'amostra' && (
                <>
                  <span style={{ fontSize: 12, color: 'var(--ink3)' }}>tamanho:</span>
                  <input
                    className="in num"
                    style={{ width: 90, padding: '5px 8px' }}
                    type="number"
                    min={1}
                    step={1}
                    value={publico.amostra}
                    onChange={(e) => {
                      // `|| 100` deixava passar negativo (`parseInt('-5')` é
                      // truthy), e amostra negativa não amostrava nada: o
                      // servidor enfileirava a base inteira. Zero sinaliza
                      // "inválido" e `motivoPublicoIncoerente` explica.
                      const n = parseInt(e.target.value, 10);
                      setPublico((p) => ({ ...p, amostra: Number.isFinite(n) && n >= 1 ? n : 0 }));
                    }}
                  />
                  <span style={{ fontSize: 12, color: 'var(--ink3)' }}>sorteados entre os opt-in</span>
                </>
              )}
              <select
                className="sel"
                value={publico.tag || ''}
                onChange={(e) => setPublico((p) => ({ ...p, tag: e.target.value || undefined }))}
              >
                <option value="">Qualquer tag</option>
                {TAGS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            <dl className="dl" style={{ marginTop: 12 }}>
              <dt>Com opt-in e ativos</dt>
              <dd className="num">
                <b>{totalPublico.toLocaleString('pt-BR')}</b>
              </dd>
              <dt>Resposta esperada</dt>
              <dd className="num">
                {historico ? (
                  <>
                    ~{Math.round(totalPublico * historico.taxa)} (
                    {Math.round(historico.taxa * 100)}% medido em {historico.campanhas} campanha
                    {historico.campanhas > 1 ? 's' : ''} do mandato: {historico.respostas} respostas
                    / {historico.enviadas.toLocaleString('pt-BR')} enviadas)
                  </>
                ) : (
                  <>
                    ~{Math.round(totalPublico * TAXA_RESPOSTA_REFERENCIA)} (
                    {Math.round(TAXA_RESPOSTA_REFERENCIA * 100)}% de referência:{' '}
                    <b>não calculado</b> sobre as campanhas do mandato: nenhuma campanha registrou
                    envio ainda, então não há taxa medida para projetar)
                  </>
                )}
              </dd>
              <dt>Bairros cobertos</dt>
              <dd>
                {bairrosCobertos.length > 6
                  ? `${bairrosCobertos.length} bairros`
                  : bairrosCobertos.map(nomeBairro).join(', ') || '—'}
              </dd>
              <dt>Leitura por</dt>
              <dd>{publico.modo === 'bairro' ? 'bairro (alimenta o Perfil do território)' : 'bairro dos respondentes + consolidado'}</dd>
              <dt>Custo estimado</dt>
              <dd className="num">
                ≈ US$ {(totalPublico * perguntasAtivas.length * 0.0068).toFixed(2)} (utility)
              </dd>
            </dl>
            {totalPublico > limiar && (
              <div className="note warn" style={{ marginTop: 10 }}>
                <b>Acima do limiar ({limiar}):</b> exige aprovação da Chefia.
              </div>
            )}
          </div>

          <div className="card">
            <h2>Enviar</h2>
            {/*
              O que está escrito aqui é o que o código faz, e nada além disso:
              o filtro de opt-in é aplicado pelo servidor
              (`campanhas.service.ts`, `calcularPublicoAlvo`) e reconferido no
              instante do envio, e a janela de silêncio e o teto mensal por
              contato são aplicados pelo worker (`outbox.worker.ts`). A
              verificação de período eleitoral vedado NÃO é feita pelo sistema
              — por isso ela aparece abaixo como lacuna, não como garantia.
            */}
            <p style={{ fontSize: 13 }} data-testid="resumo-envio">
              {perguntasAtivas.length} perguntas · {totalPublico.toLocaleString('pt-BR')} pessoas ·
              só contatos com opt-in registrado (conferido de novo no momento do envio) · janela de
              silêncio e teto mensal por contato aplicados pelo disparador.
            </p>
            {/*
              Fica visível: o resumo acima lista o que o sistema garante, e sem
              esta linha o período vedado passaria por mais uma garantia.
            */}
            <div className="note warn" style={{ marginTop: 8, fontSize: 12 }}>
              <b>Período eleitoral vedado: não verificado pelo sistema.</b> Conferir a data é
              responsabilidade do gabinete.{' '}
              <Info titulo="Por que o período vedado não é verificado">
                Não existe checagem de calendário eleitoral no código. O bloqueio só ocorre se
                alguém ligar manualmente a chave <code>BLOQUEIO_PERIODO_ELEITORAL</code> no
                servidor.
              </Info>
            </div>
            <div className="filters">
              <button type="button" className="btn" onClick={() => setEtapa(1)}>
                ← Perguntas
              </button>
              <button type="button" className="btn pri" disabled={criar.isPending} onClick={aprovarEEnviar}>
                {criar.isPending ? 'Enviando…' : 'Aprovar e enviar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {etapa === 3 && resultado && (
        <div style={{ marginTop: 12 }}>
          {/*
            A faixa de KPI é o que o olho lê primeiro, num instante em que
            existem ZERO respostas coletadas. A marca de simulação vai no
            cabeçalho da faixa E em cada indicador derivado da simulação —
            não só no card de leitura, mais abaixo.
          */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 6,
              fontSize: 12,
              color: 'var(--ink3)',
            }}
          >
            <span className="chip warn">Demonstração</span>
            <span>
              Indicadores marcados com <b>(simulado)</b> não vêm de resposta coletada. O webhook de
              respostas do WhatsApp ainda não alimentou esta campanha.
            </span>
          </div>
          <div className="grid g4">
            <div className="card kpi">
              <div className="l">Convidados</div>
              <div className="v">{resultado.convidados.toLocaleString('pt-BR')}</div>
              <div className="d">opt-in ativos · dado real</div>
            </div>
            <div className="card kpi">
              <div className="l">Respostas (simulado)</div>
              <div className="v" style={{ color: 'var(--good)' }}>
                {resultado.respostas.toLocaleString('pt-BR')}
              </div>
              <div className="d">
                {resultado.taxaResposta}% de resposta (simulado ·{' '}
                {resultado.taxaUsada.origem === 'historico'
                  ? 'taxa medida nas campanhas do mandato'
                  : 'taxa de referência, não calculada'}
                )
              </div>
            </div>
            <div className="card kpi">
              <div className="l">Perguntas</div>
              <div className="v">{resultado.porPergunta.length}</div>
              <div className="d">
                {perguntasAtivas.filter((p) => p.e === 'likert').length} de concordância (cruzáveis)
              </div>
            </div>
            <div className="card kpi">
              <div className="l">Apoio condicional (simulado)</div>
              <div className="v" style={{ color: resultado.segmentos[1] && resultado.segmentos[1].pct > 20 ? 'var(--warn)' : 'var(--ink)' }}>
                {resultado.segmentos[1] ? `${resultado.segmentos[1].pct}%` : '—'}
              </div>
              <div className="d">concorda com o fim, discorda do meio (simulado)</div>
            </div>
          </div>

          <div className="card" style={{ borderColor: '#B26A00', marginTop: 12 }}>
            <h2 style={{ color: '#B26A00' }}>Leitura do OTO · {resultado.id}</h2>
            <div
              style={{ fontSize: 13, lineHeight: 1.6 }}
              dangerouslySetInnerHTML={{ __html: cpLeitura(resultado, tema, [], nomeBairro) }}
            />
            {/*
              Fica visível: a leitura acima parece um resultado de pesquisa. Sem
              esta linha, a distribuição de respostas seria lida como coletada.
            */}
            <div className="note" style={{ marginTop: 10, fontSize: 12 }}>
              Resultado <b>simulado para demonstração</b>.{' '}
              <Info titulo="O que é simulado e o que é real nesta leitura">
                A leitura acima usa o público real ({resultado.convidados.toLocaleString('pt-BR')}{' '}
                contatos), mas a distribuição de respostas ainda não vem de dado coletado.
                Respostas reais chegam pelo webhook de WhatsApp quando a campanha estiver em
                operação.
              </Info>
            </div>
            <div className="filters" style={{ marginTop: 10 }}>
              <button type="button" className="btn sm" onClick={novaCampanha}>
                Nova campanha
              </button>
            </div>
          </div>

          <div className="grid g2" style={{ marginTop: 12 }}>
            {resultado.porPergunta.map((x, idx) => {
              const max = Math.max(1, ...x.totais);
              return (
                <div className="card" key={idx}>
                  <h2 style={{ fontSize: 14 }}>
                    {x.pergunta.q}{' '}
                    <span className="chip neutral" style={{ marginLeft: 'auto' }}>
                      {x.respostas} resp.
                    </span>
                  </h2>
                  {x.pergunta.e === 'aberta' ? (
                    <div className="empty">Respostas abertas: leitura por texto ainda não implementada.</div>
                  ) : (
                    x.opcoes.map((o, i) => (
                      <div key={o} style={{ marginTop: 8 }}>
                        <div style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between' }}>
                          <span>{o}</span>
                          <span className="num">{x.totais[i]!}</span>
                        </div>
                        <div className="bar" style={{ marginTop: 3 }}>
                          <i
                            style={{
                              width: `${(x.totais[i]! / max) * 100}%`,
                              background: x.pergunta.e === 'likert' ? 'var(--eleg)' : 'var(--accent)',
                            }}
                          />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              );
            })}
          </div>

          {resultado.segmentos.length > 0 && (
            <div className="card" style={{ marginTop: 12 }}>
              <h2>Grupos que a IA identificou (cruzamento das afirmações)</h2>
              {resultado.segmentos.map((s, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    gap: 12,
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: '1px solid var(--line)',
                    fontSize: 13,
                  }}
                >
                  <b className="num" style={{ width: 48, color: 'var(--eleg)' }}>
                    {s.pct}%
                  </b>
                  <div>
                    <b>{s.nome}</b>
                    <br />
                    <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{s.leitura}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: 12 }}>
            <ChatCampanha resultado={resultado} nomeBairro={nomeBairro} />
          </div>
        </div>
      )}
    </div>
  );
}
